import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, 
  Trash2, 
  Key, 
  Shield,
  CheckCircle2,
  Copy,
  Loader2,
  FileText,
  Server,
  Activity,
  Lock,
  Globe,
  Info,
  RefreshCw,
  Pencil,
  BookOpen,
  ChevronRight
} from 'lucide-react';
import { format } from 'date-fns';
import { useSocketOptional } from '@/contexts/SocketContext';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient as api } from '@/lib/api';
import { getErrorMessage } from '../../lib/utils';
import IdentitySelector from '@/components/ai/IdentitySelector';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-frontend/components/ui/tabs';
import { ScrollArea } from '@easyflow/ui-frontend/components/ui/scroll-area';
import { Checkbox } from '@easyflow/ui-frontend/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@easyflow/ui-frontend/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@easyflow/ui-frontend/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@easyflow/ui-frontend/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';

interface GatewayApiKey {
  id: string;
  name: string;
  description: string | null;
  key_prefix: string;
  is_active: boolean;
  scopes: string[];
  last_used_at: string | null;
  expires_at: string | null;
  created_at: string;
  // Task #941: budget per-key sui tool wiki scriventi (null = default 30/min).
  wiki_write_rpm: number | null;
}

// Tool esposto dal gateway (lista sola-lettura per la tabella).
interface GatewayAvailableTool {
  name: string;
  description?: string | null;
}

// Task #941: aggregato veloce per la colonna "Wiki / 7gg" della tabella keys.
interface WikiSummaryEntry {
  calls: number;
  ingest: number;
  page_changes: number;
  errors: number;
}

// Task #945: payload realtime emesso dal backend
// (`mcp-gateway:wiki:call` su `admin:mcp-gateway:{tenantId}`).
interface WikiCallEvent {
  tenantId: string;
  apiKeyId: string;
  toolName: string;
  status: 'success' | 'error';
  isIngest: boolean;
  isPageChange: boolean;
  isWikiWrite: boolean;
  rateLimitDenied?: boolean;
  rateLimit: { used: number; remaining: number; limit: number; resetAtMs: number } | null;
  ts: number;
}

// Task #945: snapshot rate-limit per riga (vivo finché siamo connessi).
interface LiveRateLimit {
  used: number;
  remaining: number;
  limit: number;
  resetAtMs: number;
  updatedAtMs: number;
}
interface WikiKeyStats {
  key: { id: string; key_prefix: string; name: string };
  window_days: number;
  total_wiki_calls: number;
  ingest_count: number;
  page_changes: number;
  wiki_errors: number;
  last_wiki_call_at: string | null;
  per_tool: { tool_name: string; calls: number }[];
  recent_wiki_calls: {
    id: string;
    tool_name: string;
    status: string;
    error_message: string | null;
    execution_time_ms: number;
    client_ip: string;
    created_at: string;
  }[];
}

interface AuditEntry {
  id: string;
  method: string;
  tool_name: string | null;
  status: string;
  error_message: string | null;
  execution_time_ms: number;
  client_ip: string;
  created_at: string;
}

interface KeyActivity {
  recent_calls: AuditEntry[];
  top_tools: { tool_name: string; call_count: string }[];
  errors_24h: number;
}

interface OAuthClient {
  id: string;
  client_id: string;
  client_name: string;
  description: string | null;
  redirect_uris: string[];
  scopes: string[];
  is_active: boolean;
  is_confidential: boolean;
  client_secret_prefix: string | null;
  created_at: string;
  updated_at: string;
}

interface McpScope {
  scope: string;
  category: string;
  label: string;
  description: string;
}


export default function MCPGatewayTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  // Task #945: socket realtime per i contatori wiki delle key MCP gateway.
  const socketCtx = useSocketOptional();
  const { user } = useAuth();
  const tenantId = user?.tenant_id;
  const [activeTab, setActiveTab] = useState('keys');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyDescription, setNewKeyDescription] = useState('');
  const [newKeyScopes, setNewKeyScopes] = useState<string[]>([]);
  const [newKeyActsAsUserIds, setNewKeyActsAsUserIds] = useState<string[]>([]);
  const [newKeyAllowedSenderIds, setNewKeyAllowedSenderIds] = useState<string[] | null>(null);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [expandedActivityKeyId, setExpandedActivityKeyId] = useState<string | null>(null);
  const [isCreateOAuthOpen, setIsCreateOAuthOpen] = useState(false);
  const [oauthClientName, setOauthClientName] = useState('');
  const [oauthClientDesc, setOauthClientDesc] = useState('');
  const [oauthRedirectUris, setOauthRedirectUris] = useState('');
  const [oauthSelectedScopes, setOauthSelectedScopes] = useState<string[]>([]);
  const [createdOAuthSecret, setCreatedOAuthSecret] = useState<{ client_id: string; client_secret: string } | null>(null);
  const [editTarget, setEditTarget] = useState<{ type: 'key' | 'oauth'; id: string; name: string; description: string; scopes: string[]; redirect_uris?: string[] } | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editScopesValue, setEditScopesValue] = useState<string[]>([]);
  const [editRedirectUris, setEditRedirectUris] = useState('');
  const [regeneratedSecret, setRegeneratedSecret] = useState<{ client_secret: string } | null>(null);
  const [confirmRegenTarget, setConfirmRegenTarget] = useState<string | null>(null);
  // Task #941: drawer dedicato wiki + budget per-key + nuovo state create dialog.
  const [expandedWikiKeyId, setExpandedWikiKeyId] = useState<string | null>(null);
  // Task #945: stato live (per riga) del rate-limit wiki_write — popolato
  // da `mcp-gateway:wiki:call`. Usato dal badge tabella + countdown drawer.
  const [liveRateLimits, setLiveRateLimits] = useState<Record<string, LiveRateLimit>>({});
  // Task #945: tick 1Hz per il countdown del rate-limit residuo nel drawer.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    if (!expandedWikiKeyId) return;
    const t = setInterval(() => setNowTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [expandedWikiKeyId]);
  const [newKeyWikiRpm, setNewKeyWikiRpm] = useState<string>('');
  const [editWikiRpm, setEditWikiRpm] = useState<string>('');

  const { data: apiKeys, isLoading: keysLoading } = useQuery<GatewayApiKey[]>({
    queryKey: ['mcp-gateway-keys'],
    queryFn: async () => {
      // Boundary DTO locale: cast del payload (envelope permissivo, nessun DTO shared).
      const res = await api.get('/mcp-gateway/keys');
      return (res.data as GatewayApiKey[]) || [];
    }
  });

  const { data: availableTools } = useQuery<GatewayAvailableTool[]>({
    queryKey: ['mcp-gateway-tools'],
    queryFn: async () => {
      const res = await api.get('/mcp-gateway/available-tools');
      return (res.data as GatewayAvailableTool[]) || [];
    }
  });

  const { data: keyActivity, isLoading: activityLoading } = useQuery<KeyActivity>({
    queryKey: ['mcp-gateway-activity', expandedActivityKeyId],
    queryFn: async () => {
      if (!expandedActivityKeyId) return { recent_calls: [], top_tools: [], errors_24h: 0 };
      const res = await api.get(`/mcp-gateway/keys/${expandedActivityKeyId}/activity`);
      return (res.data as KeyActivity) || { recent_calls: [], top_tools: [], errors_24h: 0 };
    },
    enabled: !!expandedActivityKeyId
  });

  // Task #941: aggregato wiki per riga key (single round-trip).
  const { data: wikiSummary } = useQuery<{ window_days: number; summary: Record<string, WikiSummaryEntry> }>({
    queryKey: ['mcp-gateway-wiki-summary'],
    queryFn: async () => {
      const res = await api.get<{ window_days: number; summary: Record<string, WikiSummaryEntry> }>('/mcp-gateway/keys/wiki-summary?days=7');
      return res.data || { window_days: 7, summary: {} };
    },
  });

  // Task #941: drawer dedicato wiki — fetch on-demand al click.
  const { data: wikiKeyStats, isLoading: wikiStatsLoading } = useQuery<WikiKeyStats>({
    queryKey: ['mcp-gateway-wiki-stats', expandedWikiKeyId],
    queryFn: async () => {
      if (!expandedWikiKeyId) throw new Error('no key');
      const res = await api.get(`/mcp-gateway/keys/${expandedWikiKeyId}/stats?days=7&limit=50`);
      return res.data as WikiKeyStats;
    },
    enabled: !!expandedWikiKeyId,
  });

  // Task #945: subscribe a `admin:mcp-gateway:{tenantId}` e ascolta gli
  // eventi `mcp-gateway:wiki:call`. Aggiorna in cache:
  //   - `mcp-gateway-wiki-summary` → +1 calls / +ingest / +page_changes /
  //     +errors per la riga della key (delta locale, niente refetch).
  //   - `liveRateLimits[apiKeyId]` → ultimo snapshot del bucket wiki_write.
  //   - se il drawer è aperto sulla key, invalida `mcp-gateway-wiki-stats`
  //     e `mcp-gateway-activity` per rinfrescare l'audit ultimi-50.
  // Debounce: invalidate al massimo ogni 1.5s per evitare bursti di refetch.
  const expandedWikiKeyIdRef = useRef<string | null>(null);
  const expandedActivityKeyIdRef = useRef<string | null>(null);
  expandedWikiKeyIdRef.current = expandedWikiKeyId;
  expandedActivityKeyIdRef.current = expandedActivityKeyId;
  const lastInvalidateRef = useRef<number>(0);

  useEffect(() => {
    if (!socketCtx || !tenantId) return;
    const room = `admin:mcp-gateway:${tenantId}`;
    socketCtx.joinRoom(room);

    const handler = (evt: WikiCallEvent) => {
      if (!evt || evt.tenantId !== tenantId || !evt.apiKeyId) return;

      // 1) snapshot live del rate-limit per badge tabella + countdown drawer.
      if (evt.rateLimit) {
        setLiveRateLimits((prev) => ({
          ...prev,
          [evt.apiKeyId]: ({ ...evt.rateLimit, updatedAtMs: Date.now() } as LiveRateLimit),
        }));
      }

      // 2) patch ottimistico del wikiSummary (no refetch) per la key.
      queryClient.setQueryData<{ window_days: number; summary: Record<string, WikiSummaryEntry> }>(
        ['mcp-gateway-wiki-summary'],
        (old) => {
          const base = old || { window_days: 7, summary: {} };
          const cur = base.summary[evt.apiKeyId] || { calls: 0, ingest: 0, page_changes: 0, errors: 0 };
          return {
            ...base,
            summary: {
              ...base.summary,
              [evt.apiKeyId]: {
                calls: cur.calls + 1,
                ingest: cur.ingest + (evt.isIngest ? 1 : 0),
                page_changes: cur.page_changes + (evt.isPageChange ? 1 : 0),
                errors: cur.errors + (evt.status === 'error' ? 1 : 0),
              },
            },
          };
        },
      );

      // 3) refetch debounced del drawer aperto (stats + activity).
      const now = Date.now();
      if (
        (expandedWikiKeyIdRef.current === evt.apiKeyId || expandedActivityKeyIdRef.current === evt.apiKeyId)
        && now - lastInvalidateRef.current > 1500
      ) {
        lastInvalidateRef.current = now;
        if (expandedWikiKeyIdRef.current === evt.apiKeyId) {
          queryClient.invalidateQueries({ queryKey: ['mcp-gateway-wiki-stats', evt.apiKeyId] });
        }
        if (expandedActivityKeyIdRef.current === evt.apiKeyId) {
          queryClient.invalidateQueries({ queryKey: ['mcp-gateway-activity', evt.apiKeyId] });
        }
      }
    };

    socketCtx.on('mcp-gateway:wiki:call', handler);
    return () => {
      socketCtx.off('mcp-gateway:wiki:call', handler);
      socketCtx.leaveRoom(room);
    };
    // socketCtx.socketVersion garantisce re-join dopo riconnessione.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- legacy guardrail #1219
  }, [socketCtx?.isConnected, socketCtx?.socketVersion, tenantId]);

  const { data: oauthClients, isLoading: oauthLoading } = useQuery<OAuthClient[]>({
    queryKey: ['mcp-gateway-oauth-clients'],
    queryFn: async () => {
      const res = await api.get('/mcp-gateway/oauth-clients');
      return (res.data as OAuthClient[]) || [];
    },
    retry: false
  });

  const { data: mcpScopesFromApi } = useQuery<McpScope[]>({
    queryKey: ['mcp-gateway-scopes'],
    queryFn: async () => {
      const res = await api.get('/mcp-gateway/scopes');
      return (res.data as McpScope[]) || [];
    },
    retry: false
  });

  const mcpScopes = mcpScopesFromApi || [];

  const createKeyMutation = useMutation({
    mutationFn: async (data: { name: string; description: string; scopes: string[]; wiki_write_rpm?: number | null; acts_as_user_ids?: string[]; allowed_sender_ids?: string[] | null }) => {
      const res = await api.post<{ api_key: string }>('/mcp-gateway/keys', data);
      return res;
    },
    onSuccess: (data) => {
      if (data.data?.api_key) setCreatedKey(data.data.api_key);
      queryClient.invalidateQueries({ queryKey: ['mcp-gateway-keys'] });
      toast({ title: 'API Key creata', description: 'Copia la chiave ora - non sarà più visibile' });
    },
    onError: (error: unknown) => {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    }
  });

  const deleteKeyMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/mcp-gateway/keys/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mcp-gateway-keys'] });
      toast({ title: 'API Key eliminata' });
    }
  });

  const toggleKeyMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      await api.patch(`/mcp-gateway/keys/${id}`, { is_active });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mcp-gateway-keys'] });
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ type, id, name, description, scopes, redirect_uris, wiki_write_rpm }: { type: 'key' | 'oauth'; id: string; name: string; description: string; scopes: string[]; redirect_uris?: string[]; wiki_write_rpm?: number | null }) => {
      if (type === 'key') {
        // Task #941: passa wiki_write_rpm SOLO se l'utente l'ha toccato
        // (undefined = no-op, null = reset al default 30/min, intero = override).
        const payload: Record<string, unknown> = { name, description, scopes };
        if (wiki_write_rpm !== undefined) payload.wiki_write_rpm = wiki_write_rpm;
        await api.patch(`/mcp-gateway/keys/${id}`, payload);
      } else {
        const uris = redirect_uris || [];
        await api.patch(`/mcp-gateway/oauth-clients/${id}`, { client_name: name, description, scopes, redirect_uris: uris });
      }
    },
    onSuccess: (_data, variables) => {
      if (variables.type === 'key') {
        queryClient.invalidateQueries({ queryKey: ['mcp-gateway-keys'] });
      } else {
        queryClient.invalidateQueries({ queryKey: ['mcp-gateway-oauth-clients'] });
      }
      setEditTarget(null);
      toast({ title: 'Salvato con successo' });
    },
    onError: (error: unknown) => {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    }
  });

  const createOAuthMutation = useMutation({
    mutationFn: async (data: { client_name: string; description: string; redirect_uris: string[]; scopes: string[] }) => {
      const res = await api.post<{ client_id: string; client_secret: string }>('/mcp-gateway/oauth-clients', data);
      return res;
    },
    onSuccess: (data) => {
      if (data.data) setCreatedOAuthSecret({ client_id: data.data.client_id, client_secret: data.data.client_secret });
      queryClient.invalidateQueries({ queryKey: ['mcp-gateway-oauth-clients'] });
      toast({ title: 'OAuth Client creato', description: 'Copia il client_secret ora - non sarà più visibile' });
    },
    onError: (error: unknown) => {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    }
  });

  const deleteOAuthMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/mcp-gateway/oauth-clients/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mcp-gateway-oauth-clients'] });
      toast({ title: 'OAuth Client eliminato e token revocati' });
    }
  });

  const toggleOAuthMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      await api.patch(`/mcp-gateway/oauth-clients/${id}`, { is_active });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mcp-gateway-oauth-clients'] });
    }
  });

  const regenerateSecretMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<{ client_secret: string }>(`/mcp-gateway/oauth-clients/${id}/regenerate-secret`, {});
      return res;
    },
    onSuccess: (data) => {
      if (data.data?.client_secret) setRegeneratedSecret({ client_secret: data.data.client_secret });
      queryClient.invalidateQueries({ queryKey: ['mcp-gateway-oauth-clients'] });
      toast({ title: 'Secret rigenerato', description: 'Tutti i token esistenti sono stati revocati. Copia il nuovo secret.' });
    },
    onError: (error: unknown) => {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    }
  });

  const handleCreateOAuth = () => {
    if (!oauthClientName.trim()) return;
    const uris = oauthRedirectUris.split('\n').map(u => u.trim()).filter(Boolean);
    createOAuthMutation.mutate({
      client_name: oauthClientName,
      description: oauthClientDesc,
      redirect_uris: uris,
      scopes: oauthSelectedScopes
    });
  };

  const toggleOAuthScope = (scope: string) => {
    setOauthSelectedScopes(prev =>
      prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope]
    );
  };

  const toggleNewKeyScope = (scope: string) => {
    setNewKeyScopes(prev =>
      prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope]
    );
  };

  const toggleEditScope = (scope: string) => {
    setEditScopesValue(prev =>
      prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope]
    );
  };

  const handleCreateKey = () => {
    if (!newKeyName.trim()) return;
    let wikiRpm: number | null | undefined = undefined;
    if (newKeyWikiRpm.trim() !== '') {
      const n = parseInt(newKeyWikiRpm, 10);
      if (Number.isFinite(n) && n >= 1 && n <= 120) wikiRpm = n;
    }
    createKeyMutation.mutate({
      name: newKeyName,
      description: newKeyDescription,
      scopes: newKeyScopes,
      wiki_write_rpm: wikiRpm,
      acts_as_user_ids: newKeyActsAsUserIds,
      allowed_sender_ids: newKeyAllowedSenderIds,
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copiato negli appunti' });
  };

  // Task #2284: porta l'utente al tab "Documentazione" e (opzionale) scrolla
  // alla sezione richiesta. Sostituisce la pagina docs separata + il wizard.
  const goToDocsSection = (anchorId?: string) => {
    setActiveTab('docs');
    if (anchorId) {
      window.setTimeout(() => {
        document.getElementById(anchorId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 60);
    }
  };

  const openEdit = (type: 'key' | 'oauth', id: string, name: string, description: string, scopes: string[], redirect_uris?: string[], wikiRpm?: number | null) => {
    setEditTarget({ type, id, name, description: description || '', scopes, redirect_uris });
    setEditName(name);
    setEditDescription(description || '');
    setEditScopesValue(scopes || []);
    setEditRedirectUris((redirect_uris || []).join('\n'));
    setEditWikiRpm(wikiRpm == null ? '' : String(wikiRpm));
  };

  const handleSaveEdit = () => {
    if (!editTarget) return;
    const uris = editRedirectUris.split('\n').map(u => u.trim()).filter(Boolean);
    // Task #941: tri-state — '' = reset (null), intero in [1,120] = override.
    let wikiRpm: number | null | undefined = undefined;
    if (editTarget.type === 'key') {
      if (editWikiRpm.trim() === '') {
        wikiRpm = null;
      } else {
        const n = parseInt(editWikiRpm, 10);
        if (Number.isFinite(n) && n >= 1 && n <= 120) wikiRpm = n;
        else {
          toast({ title: 'Valore non valido', description: 'wiki_write_rpm deve essere in [1, 120] (vuoto = default 30/min)', variant: 'destructive' });
          return;
        }
      }
    }
    updateMutation.mutate({
      type: editTarget.type,
      id: editTarget.id,
      name: editName,
      description: editDescription,
      scopes: editScopesValue,
      redirect_uris: editTarget.type === 'oauth' ? uris : undefined,
      wiki_write_rpm: wikiRpm,
    });
  };

  const renderScopeBadges = (scopes: string[]) => {
    if (!scopes || scopes.length === 0) {
      return <Badge variant="outline" className="text-xs">Tutti i tool</Badge>;
    }
    return (
      <div className="flex flex-wrap gap-1">
        {scopes.slice(0, 3).map(s => (
          <Badge key={s} variant="secondary" className="text-xs">{s.replace('mcp:', '')}</Badge>
        ))}
        {scopes.length > 3 && (
          <Badge variant="outline" className="text-xs">+{scopes.length - 3}</Badge>
        )}
      </div>
    );
  };

  const endpointUrl = `${window.location.origin}/api/mcp/gateway`;

  return (
    <div className="space-y-6">
      <Card className="border-l-2 border-l-violet-400/60 dark:border-l-violet-400/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="h-5 w-5 text-violet-500 dark:text-violet-400" />
            MCP Gateway
          </CardTitle>
          <CardDescription>
            Esponi i tools VoIP interni come server MCP per applicazioni esterne.
            Le app esterne possono chiamare i tuoi tools usando il protocollo MCP standard.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList>
              <TabsTrigger value="keys" className="flex items-center gap-2">
                <Key className="h-4 w-4" />
                API Keys
              </TabsTrigger>
              <TabsTrigger value="oauth" className="flex items-center gap-2">
                <Lock className="h-4 w-4" />
                OAuth 2.1
              </TabsTrigger>
              <TabsTrigger value="docs" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Documentazione
              </TabsTrigger>
            </TabsList>

            <TabsContent value="keys" className="space-y-4">
              {/* Task #2284: rimando alla guida unica "Quale credenziale mi serve?". */}
              <div className="flex items-start gap-2 rounded-lg border border-violet-400/30 bg-violet-50 dark:bg-violet-950/20 p-3 text-sm">
                <Info className="h-4 w-4 mt-0.5 shrink-0 text-violet-500" />
                <p className="text-muted-foreground">
                  La API key <code className="bg-muted px-1 rounded">mcp_</code> è un segreto statico per server, script e automazioni (n8n, EasyFlows, cURL). Per integrare la Company Wiki, crea una key con scope <code className="bg-muted px-1 rounded">wiki:write</code>.{' '}
                  <button type="button" onClick={() => goToDocsSection('docs-credenziali')} className="underline font-medium text-violet-600 dark:text-violet-400">
                    Quale credenziale mi serve?
                  </button>
                </p>
              </div>

              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">API Keys Gateway</h3>
                <Button onClick={() => setIsCreateOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" /> Nuova API Key
                </Button>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Prefisso</TableHead>
                    <TableHead>Scopes</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead>Ultimo Utilizzo</TableHead>
                    {/* Task #941: aggregato wiki 7 giorni — 3 micro-badge. */}
                    <TableHead>Wiki / 7gg</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {keysLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-4">
                        <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : !apiKeys?.length ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-4 text-muted-foreground">
                        Nessuna API key creata. Crea una key per iniziare.
                      </TableCell>
                    </TableRow>
                  ) : (
                    apiKeys.map((key: GatewayApiKey) => (
                      <React.Fragment key={key.id}>
                        <TableRow>
                          <TableCell className="font-medium">
                            {key.name}
                          </TableCell>
                          <TableCell>
                            <code className="text-xs bg-muted px-2 py-1 rounded">{key.key_prefix}...</code>
                          </TableCell>
                          <TableCell>
                            {renderScopeBadges(key.scopes)}
                          </TableCell>
                          <TableCell>
                            <Switch
                              checked={key.is_active}
                              onCheckedChange={(checked) => {
                                toggleKeyMutation.mutate({ id: key.id, is_active: checked });
                              }}
                            />
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {key.last_used_at 
                              ? format(new Date(key.last_used_at), 'dd/MM/yyyy HH:mm')
                              : 'Mai'}
                          </TableCell>
                          {/* Task #941: micro-badge calls / ingest / page changes (7gg).
                              Task #945: aggiunto badge live "rate-limit residuo"
                              quando una key sta consumando il bucket wiki_write
                              del minuto in corso. */}
                          <TableCell>
                            {(() => {
                              const s = wikiSummary?.summary?.[key.id];
                              const live = liveRateLimits[key.id];
                              const liveActive = !!live && live.resetAtMs > Date.now() && live.used > 0;
                              if ((!s || s.calls === 0) && !liveActive) return <span className="text-xs text-muted-foreground">—</span>;
                              return (
                                <div className="flex items-center gap-1 text-xs">
                                  <Badge variant="outline" title="Tutte le chiamate wiki_*">{s?.calls ?? 0}</Badge>
                                  <Badge variant="secondary" title="wiki_carica_documento(_da_url) + wiki_assimila_*">↑{s?.ingest ?? 0}</Badge>
                                  <Badge variant="secondary" title="wiki_crea/aggiorna/archivia/...">✎{s?.page_changes ?? 0}</Badge>
                                  {(s?.errors ?? 0) > 0 && <Badge variant="destructive" title="Errori">!{s!.errors!}</Badge>}
                                  {liveActive && (
                                    <Badge
                                      variant={live.remaining === 0 ? 'destructive' : 'outline'}
                                      title={`wiki_write: ${live.used}/${live.limit} nel minuto in corso (reset fra ${Math.max(0, Math.ceil((live.resetAtMs - Date.now()) / 1000))}s)`}
                                      className="gap-1"
                                    >
                                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                      {live.used}/{live.limit}
                                    </Badge>
                                  )}
                                </div>
                              );
                            })()}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => openEdit('key', key.id, key.name, key.description || '', key.scopes, undefined, key.wiki_write_rpm)}
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent><p>Modifica</p></TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              {/* Task #941: drawer wiki dedicato (audit ultimi 50). */}
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => setExpandedWikiKeyId(prev => prev === key.id ? null : key.id)}
                                    >
                                      <BookOpen className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent><p>Audit wiki (ultime 50)</p></TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setExpandedActivityKeyId(prev => prev === key.id ? null : key.id)}
                              >
                                <Activity className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-destructive hover:text-destructive"
                                onClick={() => {
                                  if (confirm('Eliminare questa API key? L\'accesso sarà revocato immediatamente.')) {
                                    deleteKeyMutation.mutate(key.id);
                                  }
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        {expandedActivityKeyId === key.id && (
                          <TableRow>
                            <TableCell colSpan={7} className="bg-muted/30 p-4">
                              {activityLoading ? (
                                <div className="flex items-center justify-center py-4">
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                </div>
                              ) : keyActivity ? (
                                <div className="grid grid-cols-3 gap-4">
                                  <div>
                                    <h4 className="text-sm font-medium mb-2">Errori (24h)</h4>
                                    <div className={`text-2xl font-bold ${keyActivity.errors_24h > 0 ? 'text-destructive' : 'text-green-600'}`}>
                                      {keyActivity.errors_24h}
                                    </div>
                                  </div>
                                  <div>
                                    <h4 className="text-sm font-medium mb-2">Top Tools</h4>
                                    {keyActivity.top_tools.length === 0 ? (
                                      <p className="text-sm text-muted-foreground">Nessun dato</p>
                                    ) : (
                                      <div className="space-y-1">
                                        {keyActivity.top_tools.map(t => (
                                          <div key={t.tool_name} className="flex justify-between text-sm">
                                            <span className="font-mono text-xs">{t.tool_name}</span>
                                            <Badge variant="outline">{t.call_count}</Badge>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  <div>
                                    <h4 className="text-sm font-medium mb-2">Ultime Chiamate</h4>
                                    {keyActivity.recent_calls.length === 0 ? (
                                      <p className="text-sm text-muted-foreground">Nessuna chiamata</p>
                                    ) : (
                                      <ScrollArea className="h-[120px]">
                                        <div className="space-y-1">
                                          {keyActivity.recent_calls.slice(0, 10).map(call => (
                                            <div key={call.id} className="flex items-center gap-2 text-xs">
                                              <Badge variant={call.status === 'success' ? 'secondary' : 'destructive'} className="text-[10px] px-1">
                                                {call.status === 'success' ? 'OK' : 'ERR'}
                                              </Badge>
                                              <span className="font-mono">{call.tool_name || call.method}</span>
                                              <span className="text-muted-foreground ml-auto">{call.execution_time_ms}ms</span>
                                            </div>
                                          ))}
                                        </div>
                                      </ScrollArea>
                                    )}
                                  </div>
                                </div>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        )}
                        {/* Task #941: drawer wiki dedicato — recap stats + ultime 50 chiamate. */}
                        {expandedWikiKeyId === key.id && (
                          <TableRow>
                            <TableCell colSpan={7} className="bg-primary/5 p-4">
                              {wikiStatsLoading ? (
                                <div className="flex items-center justify-center py-4">
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                </div>
                              ) : wikiKeyStats ? (
                                <div className="space-y-3">
                                  <div className="flex items-center justify-between">
                                    <h4 className="text-sm font-medium flex items-center gap-2">
                                      <BookOpen className="h-4 w-4" /> Wiki — ultimi {wikiKeyStats.window_days} giorni
                                      {key.wiki_write_rpm != null && (
                                        <Badge variant="outline" className="text-xs ml-2">budget {key.wiki_write_rpm}/min</Badge>
                                      )}
                                    </h4>
                                    <Button variant="ghost" size="sm" onClick={() => goToDocsSection('docs-wiki')}>
                                      Documentazione <ChevronRight className="h-3 w-3 ml-1" />
                                    </Button>
                                  </div>
                                  {/* Task #945: countdown live del rate-limit residuo
                                      per il minuto in corso. Si aggiorna a 1Hz e
                                      mostra "—" se non c'è ancora alcuna chiamata
                                      ricevuta via socket nel minuto corrente. */}
                                  {(() => {
                                    const live = liveRateLimits[key.id];
                                    const now = Date.now();
                                    const isLive = !!live && live.resetAtMs > now;
                                    const remainingSec = isLive ? Math.max(0, Math.ceil((live.resetAtMs - now) / 1000)) : 0;
                                    const limit = isLive ? live.limit : (key.wiki_write_rpm ?? 30);
                                    const used = isLive ? live.used : 0;
                                    const remaining = isLive ? live.remaining : limit;
                                    const pct = Math.min(100, Math.round((used / Math.max(1, limit)) * 100));
                                    const exhausted = isLive && remaining === 0;
                                    return (
                                      <div className={`rounded border p-2 ${exhausted ? 'border-destructive/50 bg-destructive/5' : 'bg-background'}`}>
                                        <div className="flex items-center justify-between text-xs">
                                          <span className="flex items-center gap-2 font-medium">
                                            <span className={`inline-block h-2 w-2 rounded-full ${socketCtx?.isConnected ? 'bg-emerald-500 animate-pulse' : 'bg-muted-foreground/40'}`} />
                                            Rate-limit wiki_write — minuto in corso
                                          </span>
                                          <span className="text-muted-foreground">
                                            reset fra <span className="font-mono">{remainingSec}s</span>
                                          </span>
                                        </div>
                                        <div className="mt-1 flex items-center justify-between text-xs">
                                          <span>
                                            <span className={`font-mono ${exhausted ? 'text-destructive font-semibold' : ''}`}>{used}</span>
                                            <span className="text-muted-foreground"> / {limit} chiamate</span>
                                          </span>
                                          <span className={`font-mono ${exhausted ? 'text-destructive' : 'text-muted-foreground'}`}>
                                            {remaining} residue
                                          </span>
                                        </div>
                                        <div className="mt-1 h-1.5 w-full overflow-hidden rounded bg-muted">
                                          <div
                                            className={`h-full transition-all ${exhausted ? 'bg-destructive' : pct > 80 ? 'bg-amber-500' : 'bg-primary'}`}
                                            style={{ width: `${pct}%` }}
                                          />
                                        </div>
                                        {!isLive && (
                                          <p className="mt-1 text-[11px] text-muted-foreground">
                                            Nessuna chiamata wiki_write nel minuto corrente. Il contatore parte appena la key esegue un tool.
                                          </p>
                                        )}
                                      </div>
                                    );
                                  })()}
                                  <div className="grid grid-cols-4 gap-3">
                                    <div className="rounded border bg-background p-2">
                                      <p className="text-xs text-muted-foreground">Chiamate wiki</p>
                                      <p className="text-xl font-semibold">{wikiKeyStats.total_wiki_calls}</p>
                                    </div>
                                    <div className="rounded border bg-background p-2">
                                      <p className="text-xs text-muted-foreground">Ingest (upload + assimila)</p>
                                      <p className="text-xl font-semibold">{wikiKeyStats.ingest_count}</p>
                                    </div>
                                    <div className="rounded border bg-background p-2">
                                      <p className="text-xs text-muted-foreground">Modifiche pagine</p>
                                      <p className="text-xl font-semibold">{wikiKeyStats.page_changes}</p>
                                    </div>
                                    <div className="rounded border bg-background p-2">
                                      <p className="text-xs text-muted-foreground">Errori</p>
                                      <p className={`text-xl font-semibold ${wikiKeyStats.wiki_errors > 0 ? 'text-destructive' : 'text-green-600'}`}>{wikiKeyStats.wiki_errors}</p>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <h5 className="text-xs font-medium text-muted-foreground mb-2">Per tool</h5>
                                      {wikiKeyStats.per_tool.length === 0 ? (
                                        <p className="text-xs text-muted-foreground">Nessun dato</p>
                                      ) : (
                                        <div className="space-y-1">
                                          {wikiKeyStats.per_tool.map(t => (
                                            <div key={t.tool_name} className="flex justify-between text-xs">
                                              <span className="font-mono">{t.tool_name}</span>
                                              <Badge variant="outline">{t.calls}</Badge>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                    <div>
                                      <h5 className="text-xs font-medium text-muted-foreground mb-2">Ultime 50 chiamate</h5>
                                      {wikiKeyStats.recent_wiki_calls.length === 0 ? (
                                        <p className="text-xs text-muted-foreground">Nessuna chiamata</p>
                                      ) : (
                                        <ScrollArea className="h-[180px]">
                                          <div className="space-y-1">
                                            {wikiKeyStats.recent_wiki_calls.map(c => (
                                              <div key={c.id} className="flex items-center gap-2 text-xs border-b border-muted/50 py-1">
                                                <Badge variant={c.status === 'success' ? 'secondary' : 'destructive'} className="text-[10px] px-1">
                                                  {c.status === 'success' ? 'OK' : 'ERR'}
                                                </Badge>
                                                <span className="font-mono">{c.tool_name}</span>
                                                <span className="text-muted-foreground ml-auto">{c.execution_time_ms}ms</span>
                                                <span className="text-muted-foreground">{format(new Date(c.created_at), 'dd/MM HH:mm')}</span>
                                              </div>
                                            ))}
                                          </div>
                                        </ScrollArea>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </TableBody>
              </Table>
            </TabsContent>

            <TabsContent value="oauth" className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-medium">OAuth 2.1 Clients</h3>
                  <p className="text-sm text-muted-foreground">
                    Crea client OAuth per integrazioni con Claude, Cursor e altri client MCP compatibili.
                  </p>
                </div>
                <Button onClick={() => setIsCreateOAuthOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" /> Nuovo Client OAuth
                </Button>
              </div>

              <div className="border rounded-lg p-4 bg-blue-50 dark:bg-blue-950/30 text-sm space-y-3">
                <div className="flex items-center gap-2 font-medium">
                  <Globe className="h-4 w-4" /> OAuth 2.1 con PKCE (S256)
                </div>
                <p className="text-muted-foreground">
                  Protocollo standard per integrazioni sicure con Claude Desktop, Cursor, Continue e altri client MCP.
                  Ogni client ottiene un <code className="bg-muted px-1 rounded">client_id</code> + <code className="bg-muted px-1 rounded">client_secret</code>, autenticazione tramite Authorization Code con PKCE S256.
                  I token di accesso sono scope-based per categoria di tool.
                </p>
                <div>
                  <p className="text-xs font-medium mb-1">Discovery URL (RFC 8414):</p>
                  <div className="flex items-center gap-2">
                    <code className="bg-muted px-2 py-1 rounded text-xs flex-1">{`${window.location.origin}/.well-known/oauth-authorization-server`}</code>
                    <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => copyToClipboard(`${window.location.origin}/.well-known/oauth-authorization-server`)}>
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div>
                    <p className="text-xs font-medium mb-1">Authorization Endpoint:</p>
                    <code className="bg-muted px-2 py-0.5 rounded text-[11px]">/mcp-auth/authorize</code>
                  </div>
                  <div>
                    <p className="text-xs font-medium mb-1">Token Endpoint:</p>
                    <code className="bg-muted px-2 py-0.5 rounded text-[11px]">/mcp-auth/token</code>
                  </div>
                  <div>
                    <p className="text-xs font-medium mb-1">Revoke Endpoint:</p>
                    <code className="bg-muted px-2 py-0.5 rounded text-[11px]">/mcp-auth/revoke</code>
                  </div>
                  <div>
                    <p className="text-xs font-medium mb-1">PKCE Method:</p>
                    <Badge variant="outline" className="text-[11px]">S256 (obbligatorio)</Badge>
                  </div>
                </div>
                <div className="border-t pt-2 text-xs text-muted-foreground">
                  Usa OAuth 2.1 per le app desktop con OAuth nativo (Claude Desktop, Cursor): token dinamico a scadenza, nessun segreto statico da gestire.{' '}
                  <button type="button" onClick={() => goToDocsSection('docs-credenziali')} className="underline font-medium">
                    Quale credenziale mi serve?
                  </button>{' '}
                  · guida completa nella tab <strong>Documentazione</strong>.
                </div>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Client ID</TableHead>
                    <TableHead>Secret</TableHead>
                    <TableHead>Scopes</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead>Creato</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {oauthLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-4">
                        <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : !oauthClients?.length ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-4 text-muted-foreground">
                        Nessun client OAuth creato. Crea un client per iniziare con OAuth 2.1.
                      </TableCell>
                    </TableRow>
                  ) : (
                    oauthClients.map((client: OAuthClient) => (
                      <TableRow key={client.id}>
                        <TableCell className="font-medium">{client.client_name}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <code className="text-xs bg-muted px-2 py-1 rounded">{client.client_id.substring(0, 20)}...</code>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(client.client_id)}>
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          {client.client_secret_prefix ? (
                            <code className="text-xs bg-muted px-2 py-1 rounded">{client.client_secret_prefix}</code>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {renderScopeBadges(client.scopes)}
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={client.is_active}
                            onCheckedChange={(checked) => toggleOAuthMutation.mutate({ id: client.id, is_active: checked })}
                          />
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {format(new Date(client.created_at), 'dd/MM/yyyy')}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => openEdit('oauth', client.id, client.client_name, client.description || '', client.scopes, client.redirect_uris)}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent><p>Modifica</p></TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setConfirmRegenTarget(client.id)}
                                    disabled={regenerateSecretMutation.isPending}
                                  >
                                    {regenerateSecretMutation.isPending ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <RefreshCw className="h-4 w-4" />
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent><p>Rigenera Secret</p></TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              onClick={() => {
                                if (confirm('Eliminare questo client OAuth? Tutti i token saranno revocati.')) {
                                  deleteOAuthMutation.mutate(client.id);
                                }
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>

              {oauthClients && oauthClients.length > 0 && (
                <div className="border rounded-lg p-4 space-y-3">
                  <h4 className="font-medium">Configurazione Claude Desktop</h4>
                  <p className="text-sm text-muted-foreground">
                    Aggiungi questo al file <code>claude_desktop_config.json</code>:
                  </p>
                  <pre className="bg-slate-950 text-slate-50 p-4 rounded-lg font-mono text-xs overflow-x-auto">
{`{
  "mcpServers": {
    "edg-voip": {
      "url": "${endpointUrl}",
      "auth": {
        "type": "oauth2",
        "authorizationUrl": "${endpointUrl.replace('/api/mcp/gateway', '')}/mcp-auth/authorize",
        "tokenUrl": "${endpointUrl.replace('/api/mcp/gateway', '')}/mcp-auth/token",
        "clientId": "${oauthClients[0]?.client_id || 'YOUR_CLIENT_ID'}",
        "scopes": ["mcp:telephony", "mcp:crm"]
      }
    }
  }
}`}
                  </pre>
                </div>
              )}
            </TabsContent>

            <TabsContent value="docs" className="space-y-4">
              <div className="space-y-6">
                <div id="docs-credenziali" className="border rounded-lg p-4 bg-violet-50 dark:bg-violet-950/20 scroll-mt-4">
                  <h3 className="text-lg font-semibold mb-1 flex items-center gap-2">
                    <Key className="h-5 w-5" /> Quale credenziale mi serve?
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Il Gateway MCP supporta due metodi di autenticazione. Scegli in base a <strong>cosa</strong> deve connettersi.
                  </p>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="border rounded-lg p-4 bg-background">
                      <div className="flex items-center gap-2 mb-2">
                        <Key className="h-4 w-4 text-violet-500" />
                        <h4 className="font-semibold">API Key <code className="text-xs bg-muted px-1 rounded">mcp_</code></h4>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        Segreto <strong>statico</strong> che non scade. Lo incolli una volta nella configurazione del servizio.
                      </p>
                      <ul className="space-y-1.5 text-sm text-muted-foreground">
                        <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-500" /> Server, script, cron, automazioni</li>
                        <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-500" /> n8n, EasyFlows, cURL, integrazioni backend</li>
                        <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-500" /> Ingest Company Wiki (scope <code className="bg-muted px-1 rounded">wiki:write</code>)</li>
                      </ul>
                      <p className="text-xs text-muted-foreground mt-3">Creala nella tab <strong>API Keys</strong>. Mostrata una sola volta alla creazione.</p>
                    </div>
                    <div className="border rounded-lg p-4 bg-background">
                      <div className="flex items-center gap-2 mb-2">
                        <Lock className="h-4 w-4 text-violet-500" />
                        <h4 className="font-semibold">OAuth 2.1 PKCE</h4>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        Token <strong>dinamici</strong> a scadenza (access 1h, refresh 30gg), ottenuti dopo consenso dell'utente. Nessun segreto statico circola.
                      </p>
                      <ul className="space-y-1.5 text-sm text-muted-foreground">
                        <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-500" /> App desktop con OAuth nativo</li>
                        <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-500" /> Claude Desktop, Cursor, Continue</li>
                        <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-500" /> Più utenti, ognuno col proprio consenso</li>
                      </ul>
                      <p className="text-xs text-muted-foreground mt-3">Crea un client nella tab <strong>OAuth 2.1</strong>. Vedi la guida completa più sotto.</p>
                    </div>
                  </div>
                </div>

                <div className="border rounded-lg p-4 bg-blue-50 dark:bg-blue-950/30">
                  <h3 className="text-lg font-semibold mb-3">Quick Start</h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Badge className="mt-0.5 shrink-0">1</Badge>
                      <div>
                        <p className="font-medium">Crea una API Key</p>
                        <p className="text-sm text-muted-foreground">Vai alla tab "API Keys" e crea una nuova chiave selezionando gli scope desiderati. Copiala subito, non sarà più visibile.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Badge className="mt-0.5 shrink-0">2</Badge>
                      <div>
                        <p className="font-medium">Testa con curl</p>
                        <p className="text-sm text-muted-foreground">Usa gli esempi qui sotto per verificare la connessione.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-2">Endpoint MCP Server</h3>
                  <div className="flex items-center gap-2">
                    <code className="bg-muted px-3 py-2 rounded-lg font-mono text-sm flex-1">
                      POST {endpointUrl}
                    </code>
                    <Button variant="outline" size="icon" onClick={() => copyToClipboard(endpointUrl)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-2">Protocollo</h3>
                  <p className="text-sm text-muted-foreground">
                    Il server utilizza il protocollo MCP 2025-03-26 con trasporto <strong>Streamable HTTP (JSON-RPC 2.0)</strong>.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-2">Autenticazione</h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    Includi la tua API Key nell'header Authorization come Bearer token:
                  </p>
                  <pre className="bg-slate-950 text-slate-50 p-4 rounded-lg font-mono text-sm overflow-x-auto">
{`Authorization: Bearer mcp_xxxxxxxx_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx`}
                  </pre>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-2">Esempio: Initialize</h3>
                  <pre className="bg-slate-950 text-slate-50 p-4 rounded-lg font-mono text-sm overflow-x-auto">
{`curl -X POST ${endpointUrl} \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -d '{
    "jsonrpc": "2.0",
    "id": 1,
    "method": "initialize"
  }'`}
                  </pre>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-2">Esempio: List Tools</h3>
                  <pre className="bg-slate-950 text-slate-50 p-4 rounded-lg font-mono text-sm overflow-x-auto">
{`curl -X POST ${endpointUrl} \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -d '{
    "jsonrpc": "2.0",
    "id": 2,
    "method": "tools/list"
  }'`}
                  </pre>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-2">Esempio: Call Tool</h3>
                  <pre className="bg-slate-950 text-slate-50 p-4 rounded-lg font-mono text-sm overflow-x-auto">
{`curl -X POST ${endpointUrl} \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -d '{
    "jsonrpc": "2.0",
    "id": 3,
    "method": "tools/call",
    "params": {
      "name": "check_extension_status",
      "arguments": {
        "extension": "1001"
      }
    }
  }'`}
                  </pre>
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Lock className="h-5 w-5" /> Guida OAuth 2.1 - Configurazione Client
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    OAuth 2.1 con PKCE S256 permette a Claude Desktop, Cursor, Continue e altri client MCP di autenticarsi in modo sicuro
                    senza gestire manualmente le API Key. Il flusso usa Authorization Code + PKCE per ottenere token di accesso con scadenza.
                  </p>

                  <div className="space-y-4">
                    <div className="border rounded-lg p-4 bg-green-50 dark:bg-green-950/20">
                      <h4 className="font-semibold mb-2">Come funziona il flusso OAuth</h4>
                      <div className="space-y-2 text-sm text-muted-foreground">
                        <div className="flex items-start gap-2">
                          <Badge className="mt-0.5 shrink-0">1</Badge>
                          <p>Crea un <strong>client OAuth</strong> nella tab "OAuth 2.1" specificando nome, redirect URI e scope (categorie di tool).</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <Badge className="mt-0.5 shrink-0">2</Badge>
                          <p>Copia il <strong>client_id</strong> e <strong>client_secret</strong> (mostrati solo alla creazione) e configurali nel tuo client MCP.</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <Badge className="mt-0.5 shrink-0">3</Badge>
                          <p>Il client MCP scopre gli endpoint tramite <code className="bg-muted px-1 rounded">/.well-known/oauth-authorization-server</code>.</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <Badge className="mt-0.5 shrink-0">4</Badge>
                          <p>L'utente viene reindirizzato alla pagina di autorizzazione di EDG VoIP dove approva i permessi richiesti.</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <Badge className="mt-0.5 shrink-0">5</Badge>
                          <p>Dopo l'approvazione, il client scambia il codice + PKCE verifier per un <strong>access_token</strong> (1h) e un <strong>refresh_token</strong> (30gg).</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <Badge className="mt-0.5 shrink-0">6</Badge>
                          <p>Il client usa l'access_token come Bearer per chiamare i tool MCP. Alla scadenza, usa il refresh_token per ottenerne uno nuovo.</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Configurazione Claude Desktop</h4>
                      <p className="text-sm text-muted-foreground mb-2">
                        Aggiungi questa configurazione al file <code className="bg-muted px-1 rounded">claude_desktop_config.json</code>:
                      </p>
                      <pre className="bg-slate-950 text-slate-50 p-4 rounded-lg font-mono text-sm overflow-x-auto">
{`{
  "mcpServers": {
    "edg-voip": {
      "url": "${endpointUrl}",
      "transport": "streamable-http",
      "oauth": {
        "client_id": "IL_TUO_CLIENT_ID",
        "client_secret": "IL_TUO_CLIENT_SECRET",
        "authorization_url": "${window.location.origin}/mcp-auth/authorize",
        "token_url": "${window.location.origin}/mcp-auth/token",
        "scopes": ["mcp:telephony", "mcp:crm"]
      }
    }
  }
}`}
                      </pre>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Configurazione Cursor</h4>
                      <p className="text-sm text-muted-foreground mb-2">
                        In Cursor, vai su Settings &gt; MCP Servers &gt; Add Server:
                      </p>
                      <pre className="bg-slate-950 text-slate-50 p-4 rounded-lg font-mono text-sm overflow-x-auto">
{`{
  "name": "EDG VoIP",
  "url": "${endpointUrl}",
  "auth": {
    "type": "oauth2",
    "discovery_url": "${window.location.origin}/.well-known/oauth-authorization-server",
    "client_id": "IL_TUO_CLIENT_ID",
    "client_secret": "IL_TUO_CLIENT_SECRET"
  }
}`}
                      </pre>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Flusso manuale con curl</h4>
                      <p className="text-sm text-muted-foreground mb-2">
                        Per testare il flusso OAuth completo manualmente:
                      </p>
                      <pre className="bg-slate-950 text-slate-50 p-4 rounded-lg font-mono text-sm overflow-x-auto">
{`# 1. Discovery - ottieni gli endpoint
curl ${window.location.origin}/.well-known/oauth-authorization-server

# 2. Autorizzazione - apri nel browser (genera PKCE prima)
# Il browser mostra la pagina di consenso
${window.location.origin}/mcp-auth/authorize?\\
  response_type=code&\\
  client_id=IL_TUO_CLIENT_ID&\\
  redirect_uri=http://localhost:3000/callback&\\
  scope=mcp:telephony%20mcp:crm&\\
  code_challenge=CHALLENGE_S256&\\
  code_challenge_method=S256

# 3. Scambia il codice per un token
curl -X POST ${window.location.origin}/mcp-auth/token \\
  -H "Content-Type: application/x-www-form-urlencoded" \\
  -d "grant_type=authorization_code&\\
      code=CODICE_RICEVUTO&\\
      client_id=IL_TUO_CLIENT_ID&\\
      client_secret=IL_TUO_CLIENT_SECRET&\\
      code_verifier=IL_TUO_VERIFIER&\\
      redirect_uri=http://localhost:3000/callback"

# 4. Usa il token per chiamare i tool
curl -X POST ${endpointUrl} \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer mcpat_xxxxx..." \\
  -d '{"jsonrpc":"2.0","id":1,"method":"tools/list"}'`}
                      </pre>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Scopes disponibili</h4>
                      <p className="text-sm text-muted-foreground mb-2">
                        Ogni scope controlla l'accesso a una categoria di tool. Se non selezioni scope alla creazione, il client/key avrà accesso a tutti i tool.
                      </p>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Scope</TableHead>
                            <TableHead>Categoria</TableHead>
                            <TableHead>Descrizione</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {mcpScopes?.map((s: McpScope) => (
                            <TableRow key={s.scope}>
                              <TableCell><code className="text-xs bg-muted px-1.5 py-0.5 rounded">{s.scope}</code></TableCell>
                              <TableCell className="text-sm font-medium">{s.label}</TableCell>
                              <TableCell className="text-sm text-muted-foreground">{s.description}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Token e sicurezza</h4>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Parametro</TableHead>
                            <TableHead>Valore</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          <TableRow>
                            <TableCell className="font-medium">Access Token durata</TableCell>
                            <TableCell>1 ora</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-medium">Refresh Token durata</TableCell>
                            <TableCell>30 giorni</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-medium">PKCE</TableCell>
                            <TableCell>S256 obbligatorio (plain non supportato)</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-medium">Client type</TableCell>
                            <TableCell>Confidential (client_secret obbligatorio)</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-medium">Token rotation</TableCell>
                            <TableCell>Refresh token ruota ad ogni utilizzo (il vecchio viene revocato)</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-medium">Prefisso access token</TableCell>
                            <TableCell><code className="bg-muted px-1 rounded">mcpat_</code></TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-medium">Prefisso refresh token</TableCell>
                            <TableCell><code className="bg-muted px-1 rounded">mcprt_</code></TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-medium">Tenant isolation</TableCell>
                            <TableCell>Ogni client e token sono legati a un singolo tenant. L'utente che autorizza deve appartenere allo stesso tenant del client.</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-2">Tools Disponibili</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Elenco dei tools MCP interni disponibili tramite il Gateway:
                  </p>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[250px]">Tool</TableHead>
                          <TableHead>Descrizione</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {availableTools && availableTools.length > 0 ? (
                          availableTools.map((tool) => (
                            <TableRow key={tool.name}>
                              <TableCell className="font-mono text-sm">{tool.name}</TableCell>
                              <TableCell className="text-sm">{tool.description}</TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={2} className="text-center py-4 text-muted-foreground">
                              <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
                              Caricamento tools...
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-2">Metodi Supportati</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Metodo</TableHead>
                        <TableHead>Descrizione</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-mono">initialize</TableCell>
                        <TableCell>Inizializza la connessione MCP (no auth richiesta)</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-mono">tools/list</TableCell>
                        <TableCell>Elenca i tools disponibili per la tua API key</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-mono">tools/call</TableCell>
                        <TableCell>Esegue un tool con i parametri specificati</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-mono">ping</TableCell>
                        <TableCell>Health check - ritorna pong</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-2">Codici Errore MCP</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[100px]">Codice</TableHead>
                        <TableHead>Significato</TableHead>
                        <TableHead>Soluzione</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-mono text-destructive">-32001</TableCell>
                        <TableCell>Authentication Error</TableCell>
                        <TableCell className="text-sm text-muted-foreground">Verifica che l'API key sia corretta e attiva</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-mono text-destructive">-32003</TableCell>
                        <TableCell>Permission Denied</TableCell>
                        <TableCell className="text-sm text-muted-foreground">Il tool non è incluso negli scope della API key. Modifica gli scope.</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-mono text-destructive">-32601</TableCell>
                        <TableCell>Method Not Found</TableCell>
                        <TableCell className="text-sm text-muted-foreground">Metodo non supportato. Usa initialize, tools/list, tools/call o ping</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-mono text-destructive">-32000</TableCell>
                        <TableCell>Rate Limit Exceeded</TableCell>
                        <TableCell className="text-sm text-muted-foreground">Troppe richieste. Attendi e riprova</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-mono text-destructive">-32600</TableCell>
                        <TableCell>Invalid Request</TableCell>
                        <TableCell className="text-sm text-muted-foreground">Formato JSON-RPC non valido</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                <div id="docs-wiki" className="border-t pt-6 scroll-mt-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <BookOpen className="h-5 w-5" /> Company Wiki via Gateway
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Carica documenti nella Company Wiki direttamente dal Gateway MCP usando una API Key con scope{' '}
                    <code className="bg-muted px-1 rounded">wiki:write</code>. I documenti vengono indicizzati e resi
                    ricercabili dagli agenti AI del tenant.
                  </p>

                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold mb-2">Tool esposti</h4>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[280px]">Tool</TableHead>
                            <TableHead>Descrizione</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          <TableRow>
                            <TableCell className="font-mono text-sm">wiki_carica_documento</TableCell>
                            <TableCell className="text-sm">Carica un documento dal contenuto testuale fornito inline (titolo + testo).</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-mono text-sm">wiki_carica_documento_da_url</TableCell>
                            <TableCell className="text-sm">Scarica e indicizza il contenuto di un URL pubblico (HTTPS-only, con protezioni SSRF).</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                      <p className="text-xs text-muted-foreground mt-2">
                        Passa <code className="bg-muted px-1 rounded">assimila: true</code> per generare subito gli embedding
                        e rendere il documento ricercabile dagli agenti; con <code className="bg-muted px-1 rounded">false</code>
                        il documento viene salvato ma non indicizzato.
                      </p>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Esempio: carica documento (cURL)</h4>
                      <pre className="bg-slate-950 text-slate-50 p-4 rounded-lg font-mono text-sm overflow-x-auto">
{`curl -X POST ${endpointUrl} \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer mcp_LA_TUA_KEY" \\
  -d '{
    "jsonrpc": "2.0",
    "id": 1,
    "method": "tools/call",
    "params": {
      "name": "wiki_carica_documento",
      "arguments": {
        "titolo": "Procedura onboarding clienti",
        "contenuto": "Testo del documento...",
        "assimila": true
      }
    }
  }'`}
                      </pre>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Claude Desktop</h4>
                      <p className="text-sm text-muted-foreground mb-2">
                        Configura il Gateway come server MCP con API Key statica (scope <code className="bg-muted px-1 rounded">wiki:write</code>):
                      </p>
                      <pre className="bg-slate-950 text-slate-50 p-4 rounded-lg font-mono text-sm overflow-x-auto">
{`{
  "mcpServers": {
    "edg-wiki": {
      "url": "${endpointUrl}",
      "transport": "streamable-http",
      "headers": {
        "Authorization": "Bearer mcp_LA_TUA_KEY"
      }
    }
  }
}`}
                      </pre>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">EasyFlows / n8n</h4>
                      <p className="text-sm text-muted-foreground mb-2">
                        Usa un nodo HTTP Request (POST) verso l'endpoint, con header Authorization e body JSON-RPC:
                      </p>
                      <pre className="bg-slate-950 text-slate-50 p-4 rounded-lg font-mono text-sm overflow-x-auto">
{`Method:  POST
URL:     ${endpointUrl}
Headers:
  Content-Type: application/json
  Authorization: Bearer mcp_LA_TUA_KEY
Body (JSON):
  {
    "jsonrpc": "2.0",
    "id": 1,
    "method": "tools/call",
    "params": {
      "name": "wiki_carica_documento_da_url",
      "arguments": {
        "url": "https://esempio.it/articolo",
        "assimila": true
      }
    }
  }`}
                      </pre>
                    </div>

                    <div className="border rounded-lg p-4 bg-emerald-50 dark:bg-emerald-950/20">
                      <h4 className="font-semibold mb-2 flex items-center gap-2">
                        <Shield className="h-4 w-4 text-emerald-600" /> Garanzie di sicurezza
                      </h4>
                      <ul className="space-y-1.5 text-sm text-muted-foreground">
                        <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-500" /> <span><strong>HTTPS-only</strong>: il fetch da URL accetta solo schema <code className="bg-muted px-1 rounded">https</code>.</span></li>
                        <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-500" /> <span><strong>Protezione SSRF</strong>: IP privati, loopback, link-local e metadata endpoint (es. <code className="bg-muted px-1 rounded">169.254.169.254</code>) sono bloccati.</span></li>
                        <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-500" /> <span><strong>Isolamento tenant</strong>: ogni documento è legato al tenant della API Key; nessun accesso cross-tenant.</span></li>
                        <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-500" /> <span><strong>Rate limit dedicato</strong>: budget <code className="bg-muted px-1 rounded">wiki_write_rpm</code> per-key (default tenant), monitorabile dal drawer della key.</span></li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Errori comuni</h4>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[100px]">HTTP</TableHead>
                            <TableHead>Significato</TableHead>
                            <TableHead>Soluzione</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          <TableRow>
                            <TableCell className="font-mono text-destructive">403</TableCell>
                            <TableCell>Scope mancante</TableCell>
                            <TableCell className="text-sm text-muted-foreground">La key non ha lo scope <code className="bg-muted px-1 rounded">wiki:write</code>. Modificala nella tab API Keys.</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-mono text-destructive">429</TableCell>
                            <TableCell>Rate limit superato</TableCell>
                            <TableCell className="text-sm text-muted-foreground">Superato il budget <code className="bg-muted px-1 rounded">wiki_write_rpm</code> del minuto. Attendi e riprova.</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-mono text-destructive">413</TableCell>
                            <TableCell>Documento troppo grande</TableCell>
                            <TableCell className="text-sm text-muted-foreground">Il contenuto supera la dimensione massima consentita. Suddividi in più documenti.</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-mono text-destructive">422</TableCell>
                            <TableCell>Payload non valido</TableCell>
                            <TableCell className="text-sm text-muted-foreground">Argomenti mancanti o URL non HTTPS / bloccato da SSRF. Verifica titolo, contenuto o URL.</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <Dialog open={isCreateOpen} onOpenChange={(open) => {
        setIsCreateOpen(open);
        if (!open) {
          setNewKeyName('');
          setNewKeyDescription('');
          setNewKeyScopes([]);
          setNewKeyWikiRpm('');
          setNewKeyActsAsUserIds([]);
          setNewKeyAllowedSenderIds(null);
          setCreatedKey(null);
        }
      }}>
        <DialogContent className="sm:max-w-[520px]">
          {!createdKey ? (
            <>
              <DialogHeader>
                <DialogTitle>Crea API Key Gateway</DialogTitle>
                <DialogDescription>
                  Genera una nuova chiave per permettere ad app esterne di accedere ai tuoi tools MCP.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    id="name"
                    placeholder="es. CRM Integration"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="description">Descrizione (opzionale)</Label>
                  <Input
                    id="description"
                    placeholder="es. Accesso per il sistema CRM aziendale"
                    value={newKeyDescription}
                    onChange={(e) => setNewKeyDescription(e.target.value)}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Scopes (categorie tool)</Label>
                  <p className="text-xs text-muted-foreground">Seleziona le categorie di tool a cui questa key potrà accedere. Nessuna selezione = accesso a tutti.</p>
                  <ScrollArea className="h-[200px] border rounded-md p-3">
                    <div className="space-y-2">
                      {mcpScopes?.map((s: McpScope) => (
                        <div key={s.scope} className="flex items-center space-x-2">
                          <Checkbox
                            id={`key-scope-${s.scope}`}
                            checked={newKeyScopes.includes(s.scope)}
                            onCheckedChange={() => toggleNewKeyScope(s.scope)}
                          />
                          <label htmlFor={`key-scope-${s.scope}`} className="flex-1 cursor-pointer">
                            <span className="text-sm font-medium">{s.label}</span>
                            <span className="text-xs text-muted-foreground ml-2">({s.scope})</span>
                            <p className="text-xs text-muted-foreground">{s.description}</p>
                          </label>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
                {/* Task #941: budget per-minuto sui tool wiki scriventi. */}
                <div className="grid gap-2">
                  <Label htmlFor="wiki-rpm">Wiki write rate-limit (per minuto)</Label>
                  <Input
                    id="wiki-rpm"
                    type="number"
                    min={1}
                    max={120}
                    placeholder="vuoto = default 30/min"
                    value={newKeyWikiRpm}
                    onChange={(e) => setNewKeyWikiRpm(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">Solo i tool wiki_* che scrivono (carica/crea/aggiorna/...). Lascia vuoto per il default condiviso.</p>
                </div>
                <IdentitySelector
                  value={{ acts_as_user_ids: newKeyActsAsUserIds, allowed_sender_ids: newKeyAllowedSenderIds }}
                  canTenantWide={user?.role === 'admin' || user?.role === 'super_admin'}
                  onChange={(next) => {
                    setNewKeyActsAsUserIds(next.acts_as_user_ids);
                    setNewKeyAllowedSenderIds(next.allowed_sender_ids);
                  }}
                />
              </div>
              <DialogFooter>
                <Button onClick={handleCreateKey} disabled={createKeyMutation.isPending || !newKeyName.trim()}>
                  {createKeyMutation.isPending ? 'Creazione...' : 'Crea Key'}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center text-green-600">
                  <CheckCircle2 className="mr-2 h-5 w-5" /> API Key Creata
                </DialogTitle>
                <DialogDescription>
                  Copia la chiave ora. Per sicurezza, <span className="font-bold text-destructive">non sarà più visibile</span>.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <div className="flex items-center space-x-2">
                  <code className="relative rounded bg-muted px-3 py-2 font-mono text-xs flex-1 break-all">
                    {createdKey}
                  </code>
                  <Button size="icon" variant="outline" onClick={() => copyToClipboard(createdKey)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setIsCreateOpen(false);
                  setCreatedKey(null);
                  setNewKeyName('');
                  setNewKeyDescription('');
                  setNewKeyScopes([]);
                  setNewKeyWikiRpm('');
                }}>
                  Chiudi
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={isCreateOAuthOpen} onOpenChange={(open) => {
        setIsCreateOAuthOpen(open);
        if (!open) {
          setOauthClientName('');
          setOauthClientDesc('');
          setOauthRedirectUris('');
          setOauthSelectedScopes([]);
          setCreatedOAuthSecret(null);
        }
      }}>
        <DialogContent className="sm:max-w-[520px]">
          {!createdOAuthSecret ? (
            <>
              <DialogHeader>
                <DialogTitle>Crea Client OAuth 2.1</DialogTitle>
                <DialogDescription>
                  Genera credenziali OAuth per integrare Claude, Cursor o altri client MCP con PKCE.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="oauth-name">Nome Client</Label>
                  <Input
                    id="oauth-name"
                    placeholder="es. Claude Desktop"
                    value={oauthClientName}
                    onChange={(e) => setOauthClientName(e.target.value)}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="oauth-desc">Descrizione (opzionale)</Label>
                  <Input
                    id="oauth-desc"
                    placeholder="es. Integrazione Claude Desktop personale"
                    value={oauthClientDesc}
                    onChange={(e) => setOauthClientDesc(e.target.value)}
                  />
                </div>
                <div className="grid gap-2">
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor="oauth-uris">Redirect URI (opzionale)</Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-[300px]">
                          <p className="text-xs">
                            <strong>Cos'è?</strong> L'URL dove il client viene reindirizzato dopo l'autorizzazione OAuth.
                          </p>
                          <p className="text-xs mt-1">
                            <strong>Claude Web / API:</strong> non richiede Redirect URI, lascia vuoto.
                          </p>
                          <p className="text-xs mt-1">
                            <strong>Claude Desktop:</strong> <code className="text-[10px] bg-muted px-1 rounded">http://localhost</code>
                          </p>
                          <p className="text-xs mt-1">
                            <strong>Cursor:</strong> <code className="text-[10px] bg-muted px-1 rounded">http://localhost:3000/callback</code>
                          </p>
                          <p className="text-xs mt-1">
                            <strong>App custom:</strong> il tuo URL di callback OAuth (es. <code className="text-[10px] bg-muted px-1 rounded">https://tuaapp.com/oauth/callback</code>)
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <textarea
                    id="oauth-uris"
                    className="flex min-h-[50px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                    placeholder="Lascia vuoto per Claude Web/API. Per Desktop: http://localhost"
                    value={oauthRedirectUris}
                    onChange={(e) => setOauthRedirectUris(e.target.value)}
                    rows={2}
                  />
                  <p className="text-xs text-muted-foreground">Se usi Claude Web o via API, non serve. Una URI per riga se ne servono più di una.</p>
                </div>
                <div className="grid gap-2">
                  <Label>Scopes (categorie tool)</Label>
                  <p className="text-xs text-muted-foreground">Seleziona le categorie di tool a cui questo client potrà accedere. Nessuna selezione = accesso a tutti.</p>
                  <ScrollArea className="h-[200px] border rounded-md p-3">
                    <div className="space-y-2">
                      {mcpScopes?.map((s: McpScope) => (
                        <div key={s.scope} className="flex items-center space-x-2">
                          <Checkbox
                            id={`scope-${s.scope}`}
                            checked={oauthSelectedScopes.includes(s.scope)}
                            onCheckedChange={() => toggleOAuthScope(s.scope)}
                          />
                          <label htmlFor={`scope-${s.scope}`} className="flex-1 cursor-pointer">
                            <span className="text-sm font-medium">{s.label}</span>
                            <span className="text-xs text-muted-foreground ml-2">({s.scope})</span>
                            <p className="text-xs text-muted-foreground">{s.description}</p>
                          </label>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleCreateOAuth} disabled={createOAuthMutation.isPending || !oauthClientName.trim()}>
                  {createOAuthMutation.isPending ? 'Creazione...' : 'Crea Client'}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center text-green-600">
                  <CheckCircle2 className="mr-2 h-5 w-5" /> Client OAuth Creato
                </DialogTitle>
                <DialogDescription>
                  Copia le credenziali ora. Il <span className="font-bold text-destructive">client_secret non sarà più visibile</span>.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4 space-y-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Client ID</Label>
                  <div className="flex items-center space-x-2">
                    <code className="rounded bg-muted px-3 py-2 font-mono text-xs flex-1 break-all">
                      {createdOAuthSecret.client_id}
                    </code>
                    <Button size="icon" variant="outline" onClick={() => copyToClipboard(createdOAuthSecret.client_id)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-destructive font-medium">Client Secret</Label>
                  <div className="flex items-center space-x-2">
                    <code className="rounded bg-muted px-3 py-2 font-mono text-xs flex-1 break-all">
                      {createdOAuthSecret.client_secret}
                    </code>
                    <Button size="icon" variant="outline" onClick={() => copyToClipboard(createdOAuthSecret.client_secret)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setIsCreateOAuthOpen(false);
                  setCreatedOAuthSecret(null);
                  setOauthClientName('');
                  setOauthClientDesc('');
                  setOauthRedirectUris('');
                  setOauthSelectedScopes([]);
                }}>
                  Chiudi
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTarget} onOpenChange={(open) => {
        if (!open) setEditTarget(null);
      }}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Modifica {editTarget?.type === 'key' ? 'API Key' : 'Client OAuth'}
            </DialogTitle>
            <DialogDescription>
              Modifica nome, descrizione{editTarget?.type === 'oauth' ? ', redirect URI' : ''} e scopes.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label>Nome</Label>
              <Input value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Nome" />
            </div>
            <div className="grid gap-2">
              <Label>Descrizione</Label>
              <Input value={editDescription} onChange={(e) => setEditDescription(e.target.value)} placeholder="Descrizione (opzionale)" />
            </div>
            {editTarget?.type === 'oauth' && (
              <div className="grid gap-2">
                <Label>Redirect URI</Label>
                <textarea
                  className="flex min-h-[50px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                  placeholder="Una URI per riga"
                  value={editRedirectUris}
                  onChange={(e) => setEditRedirectUris(e.target.value)}
                  rows={2}
                />
              </div>
            )}
            <div className="grid gap-2">
              <Label>Scopes (categorie tool)</Label>
              <p className="text-xs text-muted-foreground">Nessuna selezione = accesso a tutti i tool.</p>
              <ScrollArea className="h-[200px] border rounded-md p-3">
                <div className="space-y-2">
                  {mcpScopes?.map((s: McpScope) => (
                    <div key={s.scope} className="flex items-center space-x-2">
                      <Checkbox
                        id={`edit-scope-${s.scope}`}
                        checked={editScopesValue.includes(s.scope)}
                        onCheckedChange={() => toggleEditScope(s.scope)}
                      />
                      <label htmlFor={`edit-scope-${s.scope}`} className="flex-1 cursor-pointer">
                        <span className="text-sm font-medium">{s.label}</span>
                        <span className="text-xs text-muted-foreground ml-2">({s.scope})</span>
                        <p className="text-xs text-muted-foreground">{s.description}</p>
                      </label>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
            {/* Task #941: budget per-minuto sui tool wiki scriventi (solo per le API key). */}
            {editTarget?.type === 'key' && (
              <div className="grid gap-2">
                <Label htmlFor="edit-wiki-rpm">Wiki write rate-limit (per minuto)</Label>
                <Input
                  id="edit-wiki-rpm"
                  type="number"
                  min={1}
                  max={120}
                  placeholder="vuoto = default 30/min"
                  value={editWikiRpm}
                  onChange={(e) => setEditWikiRpm(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">Lascia vuoto per usare il default condiviso (30/min). Range: 1..120.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>Annulla</Button>
            <Button onClick={handleSaveEdit} disabled={updateMutation.isPending || !editName.trim()}>
              {updateMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle2 className="mr-2 h-4 w-4" />
              )}
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmRegenTarget} onOpenChange={(open) => {
        if (!open) setConfirmRegenTarget(null);
      }}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <RefreshCw className="h-5 w-5" />
              Rigenerare Client Secret?
            </DialogTitle>
            <DialogDescription>
              Il secret attuale verrà invalidato e <span className="font-semibold text-destructive">tutti i token di accesso esistenti saranno revocati</span>. I client dovranno autenticarsi di nuovo con il nuovo secret.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRegenTarget(null)}>Annulla</Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (confirmRegenTarget) {
                  regenerateSecretMutation.mutate(confirmRegenTarget);
                  setConfirmRegenTarget(null);
                }
              }}
              disabled={regenerateSecretMutation.isPending}
            >
              {regenerateSecretMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Rigenera Secret
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!regeneratedSecret} onOpenChange={(open) => {
        if (!open) setRegeneratedSecret(null);
      }}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="flex items-center text-green-600">
              <CheckCircle2 className="mr-2 h-5 w-5" /> Secret Rigenerato
            </DialogTitle>
            <DialogDescription>
              Copia il nuovo secret ora. <span className="font-bold text-destructive">Non sarà più visibile</span>. Tutti i token esistenti sono stati revocati.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-xs text-destructive font-medium">Nuovo Client Secret</Label>
            <div className="flex items-center space-x-2 mt-1">
              <code className="rounded bg-muted px-3 py-2 font-mono text-xs flex-1 break-all">
                {regeneratedSecret?.client_secret}
              </code>
              <Button size="icon" variant="outline" onClick={() => regeneratedSecret && copyToClipboard(regeneratedSecret.client_secret)}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegeneratedSecret(null)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
